<template>
  <div class="card">
    <h3 class="card-title">{{ $t('colorPicker.title') }}</h3>
    
    <div v-if="!hasImage" class="no-image">
      <p>{{ $t('colorPicker.noImage') }}</p>
    </div>
    
    <div v-else class="color-picker">
      <div class="coordinate-inputs">
        <div class="coordinate-group">
          <label for="pixelX">{{ $t('colorPicker.x') }}</label>
          <input
            id="pixelX"
            type="number"
            v-model.number="coordinates.x"
            :min="0"
            :max="maxX"
            placeholder="0"
          />
          <small v-if="!isValidCoordinates" class="hint">{{ $t('colorPicker.range') }} 0 ~ {{ maxX }}</small>
        </div>
        
        
        
        <div class="coordinate-group">
          <label for="pixelY">{{ $t('colorPicker.y') }}</label>
          <input
            id="pixelY"
            type="number"
            v-model.number="coordinates.y"
            :min="0"
            :max="maxY"
            placeholder="0"
          />
          <small v-if="!isValidCoordinates" class="hint">{{ $t('colorPicker.range') }} 0 ~ {{ maxY }}</small>
        </div>
        
        <button
          class="btn-primary"
          @click="pickColor"
          :disabled="!isValidCoordinates || !extractedColors || isExtracting || isPicking"
        >
          <span v-if="isExtracting">{{ $t('colorPicker.extracting') }}</span>
          <span v-else-if="isPicking">{{ $t('colorPicker.picking') }}</span>
          <span v-else>{{ $t('colorPicker.getColor') }}</span>
        </button>
      </div>
      
      <div v-if="isExtracting" class="inline-status">
        <span class="mini-spinner"></span>
        <span>{{ $t('colorPicker.extracting') }}</span>
      </div>
      
      <div v-if="selectedColor" class="color-result">
        <h4>{{ $t('colorPicker.colorInfo') }}</h4>
        <div class="color-display">
          <div
            class="color-swatch large"
            :style="{ backgroundColor: selectedColor.hex }"
          ></div>
          <div class="color-details">
            <div class="color-values">
              <div class="value-row">
                <span class="label">{{ $t('colorPicker.rgb') }}</span>
                <span class="value">{{ selectedColor.r }}, {{ selectedColor.g }}, {{ selectedColor.b }}</span>
              </div>
              <div class="value-row">
                <span class="label">{{ $t('colorPicker.hex') }}</span>
                <span class="value hex">{{ selectedColor.hex }}</span>
              </div>
              <div class="value-row">
                <span class="label">{{ $t('colorPicker.coordinates') }}:</span>
                <span class="value">({{ coordinates.x }}, {{ coordinates.y }})</span>
              </div>
            </div>
            
            <div v-if="closestCustomColor" class="closest-match">
              <h5>{{ $t('colorPicker.closestColor') }}</h5>
              <div class="match-display">
                <div
                  class="color-swatch"
                  :style="{ backgroundColor: closestCustomColor.hex }"
                ></div>
                <div class="match-info">
                  <div class="match-name">
                    {{ closestCustomColor.name || `颜色 #${closestCustomColor.id}` }}
                  </div>
                  <div class="match-values">
                    RGB({{ closestCustomColor.r }}, {{ closestCustomColor.g }}, {{ closestCustomColor.b }})
                  </div>
                  <div class="match-distance">
                    {{ $t('colorPicker.similarity') }} {{ colorDistanceValue.toFixed(2) }}
                    <span class="algorithm-tag">
                      ({{ getAlgorithmDisplayName(colorStore.colorMatchingAlgorithm) }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else-if="hasAttemptedPick" class="no-color">
        <p>{{ $t('colorPicker.range') }} 0 ~ {{ maxX }} {{ $t('colorPicker.range') }} 0 ~ {{ maxY }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { watch } from 'vue'
import { useColorStore } from '../stores/colorStore'
// import { hexToRgb } from '../utils/colorUtils'
import { 
  colorDistance, 
  colorDistanceLab, 
  colorDistanceWeightedRGB, 
  colorDistanceHSV, 
  colorDistanceCIEDE2000 
} from '../utils/colorUtils'

const colorStore = useColorStore()

const coordinates = ref({ x: 0, y: 0 })
const selectedColor = ref<{ r: number; g: number; b: number; hex: string } | null>(null)
const hasAttemptedPick = ref(false)

const hasImage = computed(() => colorStore.hasImage)
const isExtracting = computed(() => colorStore.isExtracting)
const isPicking = ref(false)
const extractedColors = computed(() => colorStore.extractedColors)
const customColors = computed(() => colorStore.customColors)

// 底色设置改到“颜色归并设置”中统一输入与应用

const maxX = computed(() => {
  return extractedColors.value ? extractedColors.value.width - 1 : 0
})

const maxY = computed(() => {
  return extractedColors.value ? extractedColors.value.height - 1 : 0
})

const isValidCoordinates = computed(() => {
  return coordinates.value.x >= 0 && 
         coordinates.value.x <= maxX.value &&
         coordinates.value.y >= 0 && 
         coordinates.value.y <= maxY.value
})

// 坐标超出范围时，隐藏上一次结果并提示
watch(() => ({ x: coordinates.value.x, y: coordinates.value.y }), () => {
  if (!isValidCoordinates.value) {
    selectedColor.value = null
    hasAttemptedPick.value = true
  }
})

const closestCustomColor = computed(() => {
  if (!selectedColor.value || customColors.value.length === 0) return null
  
  // 使用当前选择的算法
  const algorithm = colorStore.colorMatchingAlgorithm
  
  let closestColor = customColors.value[0]
  let minDistance = getColorDistance(selectedColor.value, closestColor, algorithm)

  for (let i = 1; i < customColors.value.length; i++) {
    const distance = getColorDistance(selectedColor.value, customColors.value[i], algorithm)
    
    if (distance < minDistance) {
      minDistance = distance
      closestColor = customColors.value[i]
    }
  }

  return closestColor
})

const colorDistanceValue = computed(() => {
  if (!selectedColor.value || !closestCustomColor.value) return 0
  
  const algorithm = colorStore.colorMatchingAlgorithm
  
  return getColorDistance(selectedColor.value, closestCustomColor.value, algorithm)
})

async function pickColor() {
  if (!isValidCoordinates.value || !extractedColors.value) return
  
  hasAttemptedPick.value = true
  isPicking.value = true
  try {
    // 微延迟以展示反馈
    await new Promise(r => setTimeout(r))
    const color = extractedColors.value.pixels[coordinates.value.y][coordinates.value.x]
    if (color) {
      selectedColor.value = color
    }
  } finally {
    isPicking.value = false
  }
}

// 根据算法计算颜色距离
function getColorDistance(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number },
  algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'
): number {
  switch (algorithm) {
    case 'rgb':
      return colorDistance(color1, color2)
    case 'lab':
      return colorDistanceLab(color1, color2)
    case 'weighted-rgb':
      return colorDistanceWeightedRGB(color1, color2)
    case 'hsv':
      return colorDistanceHSV(color1, color2)
    case 'ciede2000':
      return colorDistanceCIEDE2000(color1, color2)
    default:
      return colorDistance(color1, color2)
  }
}

// 获取算法显示名称
function getAlgorithmDisplayName(algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'): string {
  switch (algorithm) {
    case 'rgb':
      return 'RGB'
    case 'lab':
      return 'CIELAB'
    case 'weighted-rgb':
      return '加权RGB'
    case 'hsv':
      return 'HSV'
    case 'ciede2000':
      return 'CIEDE2000'
    default:
      return 'RGB'
  }
}
</script>

<style scoped>
.no-image {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.color-picker {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.coordinate-inputs {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.coordinate-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.coordinate-group label {
  font-weight: 500;
  color: #495057;
}

.coordinate-group input {
  width: 100px;
  text-align: center;
}

.color-result h4 {
  margin-bottom: 1rem;
  color: #495057;
}

.color-display {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.color-swatch {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.color-swatch.large {
  width: 80px;
  height: 80px;
}

.color-details {
  flex: 1;
}

.color-values {
  margin-bottom: 1.5rem;
}

.value-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.label {
  font-weight: 500;
  color: #495057;
  min-width: 60px;
}

.value {
  font-family: monospace;
  color: #333;
}

.value.hex {
  font-weight: 600;
  color: #667eea;
}

.closest-match h5 {
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 1rem;
}

.match-display {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.match-info {
  flex: 1;
}

.match-name {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.25rem;
}

.match-values {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.match-distance {
  font-size: 0.875rem;
  color: #28a745;
  font-weight: 500;
}

.algorithm-tag {
  background: #667eea;
  color: white;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.no-color {
  text-align: center;
  padding: 1rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 6px;
}

.hint { color: #6c757d; font-size: 0.75rem; }
.inline-status { display:flex; align-items:center; gap:.5rem; color:#6c757d; }
.mini-spinner { width:14px; height:14px; border:2px solid #e9ecef; border-top-color:#667eea; border-radius:50%; animation: spin .8s linear infinite; }
@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
</style>
